<script setup lang="ts">
import { CustomObject } from '@commercetools/platform-sdk';
import { dollars } from '@nuts/auto-delivery-sdk/dist/utils/money';
import {
  breakpointsTailwind,
  templateRef,
  useBreakpoints,
  useIntersectionObserver,
  useMediaQuery,
  useTimeoutFn,
  watchDebounced,
  whenever,
} from '@vueuse/core';
import { useHead } from '@vueuse/head';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { storeToRefs } from 'pinia';
import {
  computed,
  inject,
  onMounted,
  onServerPrefetch,
  onUnmounted,
  reactive,
  ref,
  watch,
  watchEffect,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Store, useStore } from 'vuex';

import { fetchGreetingCards } from '@/api/customProducts';
import { getPredictedShippingCutoff, ShippingCutoff } from '@/api/predictedShipping';
import { productTagsByIds, TagReference } from '@/api/productTagReferences';
import AutoDeliveryAdjustFrequencyModal from '@/components/auto-delivery/AutoDeliveryAdjustFrequencyModal.vue';
import AutoDeliveryModal, {
  AutoDeliveryModalSteps,
} from '@/components/auto-delivery/AutoDeliveryModal.vue';
import CreditCardLogo from '@/components/base/assets/CreditCardLogo.vue';
import GlobalErrorNotification from '@/components/base/GlobalErrorNotification.vue';
import Container from '@/components/base/layout/Container.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import ToastNotification from '@/components/base/ToastNotification.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import Header3 from '@/components/base/typography/Header3.vue';
import Header4 from '@/components/base/typography/Header4.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';
import ActionBlock from '@/components/cart/ActionBlock.vue';
import AdminDiscountCodeForm from '@/components/cart/AdminDiscountCodeForm.vue';
import CostSummary from '@/components/cart/CostSummary.vue';
import GreetingCardsModal, {
  Models as GreetingCardsModalModels,
  Props as GreetingCardsModalProps,
} from '@/components/cart/GreetingCardsModal.vue';
import GreetingCardUpsell from '@/components/cart/GreetingCardUpsell.vue';
import LineItem, { RemoveMode } from '@/components/cart/LineItem.vue';
import CostSummaryRedesign from '@/components/cart/redesign/CostSummary.vue';
import ShippingMessage from '@/components/cart/ShippingMessage.vue';
import ShippingPredictor from '@/components/cart/ShippingPredictor.vue';
import SupportInfo from '@/components/cart/SupportInfo.vue';
import ProductRecommendations from '@/components/recommendations/ProductRecommendations.vue';
import { useRecommendationsFromConfig } from '@/composables/dynamic-yield/recommendationsFromConfig';
import { useCallback } from '@/composables/useCallback';
import { useCart } from '@/composables/useCart';
import { useCheckout } from '@/composables/useCheckout';
import { useDelivery } from '@/composables/useDelivery';
import { useDiscount } from '@/composables/useDiscount';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { useLoginModal } from '@/composables/useLoginModal';
import { usePayment } from '@/composables/usePayment';
import { CartDiscountWithSiteMessages } from '@/lib/cart/discounts';
import {
  containsGiftPurchase,
  filterByAutoDeliveryStatus,
  getAutoDeliveryInterval,
  NutsLineItem,
} from '@/lib/cart/lineItem';
import { useAutoDelivery } from '@/stores/autoDelivery';
import { useChat } from '@/stores/chat';
import { useCustomer } from '@/stores/customer';
import { useNotifications } from '@/stores/notifications';
import { useSession } from '@/stores/session';
import { sendCartViewedEvent } from '@/utils/analytics/cartViewedEvent';
import { getAutoDeliveryDetailsPath } from '@/utils/autoDelivery';
import {
  adaptDynamicDiscountMessaging,
  getCartId,
  isGiftLineItem,
  isGreetingCardLineItem,
} from '@/utils/cart';
import {
  analyticsMetadata,
  decisionId,
  recommendations,
  title,
} from '@/utils/dynamic-yield/recommendationsRow';
import { scrollToTarget } from '@/utils/scrollTo';

const { handleOpen } = useLoginModal();

const isMobile = useBreakpoints(breakpointsTailwind).smaller('md');

dayjs.extend(advancedFormat);

const isAdmin = inject('isAdmin', false);

useHead({ title: 'Your Cart — Nuts.com' });
const route = useRoute();
const router = useRouter();
const store = useStore();

const autoDeliveryStore = useAutoDelivery();
const { activeSubscriptionsBySKU, autoDeliveryChannel, nextDelivery } =
  storeToRefs(autoDeliveryStore);
const {
  cart,
  cartKey,
  clearCart,
  containsAutoDelivery,
  containsProductDiscounts,
  deleteCart,
  loadCart,
  loadLineItemExpansions,
  maxQuantity,
  preCheckoutLineItems: lineItems,
  removeLineItem,
  updateLineItem,
  updateLineItemFailure,
} = useCart(store);
const { cxMode } = useCheckout(store);
const customerStore = useCustomer();
const { freeShippingThreshold } = useDelivery(store);
const { appliedCartDiscountIds, cartDiscountsForMessages } = useDiscount(store);
const { flags } = useFeatureFlags();
const { setNotification } = useNotifications();
const {
  cartDiscountAmount,
  combineSavingsAmounts,
  estimatedAmountToCharge,
  estimatedTotal,
  preCheckoutSubtotal,
} = usePayment(store, ref(false));

const combinedSavings = computed(() =>
  flags.strikethroughPricing ? combineSavingsAmounts(lineItems.value) : undefined,
);

type RemovedLineItem = NutsLineItem & { index: number; removeMode: RemoveMode };

const removedLineItem = ref<RemovedLineItem>();
const maybeReversedLineItemsWithRemoved = computed(() => {
  if (!flags.cartLayoutUpdates) return lineItems.value;

  const reversedLineItems = [...lineItems.value].reverse();
  if (removedLineItem.value) {
    reversedLineItems.splice(removedLineItem.value.index, 0, removedLineItem.value);
  }
  return reversedLineItems;
});

const removalTimeout = useTimeoutFn(() => {
  if (lineItems.value.length < 1) {
    // If the cart is empty, we don't show the removed line item.
    removedLineItem.value = undefined;
  } else if (removedLineItem.value) {
    removedLineItem.value.removeMode = 'removed';
  }
}, 500);

async function handleRemoveLineItem(lineItem: NutsLineItem, templateIndex: number) {
  if (flags.cartLayoutUpdates) {
    let index = templateIndex;
    if (removedLineItem.value && index > removedLineItem.value.index) {
      index -= 1;
    }
    removedLineItem.value = { ...lineItem, index, removeMode: 'removing' };
    removalTimeout.start();
  }

  return removeLineItem(lineItem.id);
}

const autoDeliverySignUp = reactive({
  interval: 6,
  itemId: undefined as string | undefined,
  step: 'sign-up' as AutoDeliveryModalSteps,
});
const freeShippingQualified = ref(false);
const shownModal = ref<'ad-sign-up' | 'ad-adjust'>();

const belowThresholdMessage = computed(() => {
  let discount: CartDiscountWithSiteMessages | undefined;
  if (dollars(cartDiscountAmount.value)) {
    const latestAppliedTier: string | undefined =
      appliedCartDiscountIds.value[appliedCartDiscountIds.value.length - 1];
    if (latestAppliedTier) {
      let index = cartDiscountsForMessages.value.findIndex((d) => d.id === latestAppliedTier);
      if (index === cartDiscountsForMessages.value.length - 1) return '';
      if (index > -1) index += 1;
      discount = cartDiscountsForMessages.value[index];
    }
  }
  if (!discount) {
    discount = cartDiscountsForMessages.value.find((d) => d.custom?.fields.belowThresholdMessage);
  }
  if (!discount?.custom?.fields.belowThresholdMessage) return '';
  return adaptDynamicDiscountMessaging(
    discount.custom.fields.belowThresholdMessage,
    discount.cartPredicate,
    preCheckoutSubtotal.value,
  );
});

const autoDeliveryLineItems = computed(() => filterByAutoDeliveryStatus(lineItems.value, true));
const autoDeliveryInterval = computed({
  get: () => {
    const lineItem = autoDeliveryLineItems.value[0];
    return lineItem ? getAutoDeliveryInterval(lineItem) : undefined;
  },
  set(value) {
    autoDeliveryLineItems.value.forEach((item) => {
      updateLineItem(item.id, {
        autoDeliveryInterval: value ? value * 7 : null,
        autoDeliveryOfferLocation: value ? 'Cart' : null,
        autoDeliveryOfferType: value ? 'Standard' : null,
        distributionChannelKey: value ? autoDeliveryChannel.value.key : null,
      });
    });
  },
});

const autoDeliverySignUpLineItem = computed(() =>
  lineItems.value.find((li) => li.id === autoDeliverySignUp.itemId),
);
const activeSubscriptionForSignUpLineItem = computed(() => {
  const sku = autoDeliverySignUpLineItem.value?.variant.sku;
  return sku ? activeSubscriptionsBySKU.value.get(sku)?.[0] : undefined;
});

const showLinkForAutoDeliverySignUpLineItem = computed(
  () =>
    !autoDeliverySignUpLineItem.value?.variant.attributes?.find(
      (a) => a.name === 'hiddenFromSearch',
    )?.value,
);

const { userFirstName } = storeToRefs(useSession());

watch(
  belowThresholdMessage,
  (message) => {
    setNotification({ key: 'couponMessage', message, messageType: 'notices' });
  },
  { deep: true, immediate: true },
);

const onClearCart = async () => {
  // eslint-disable-next-line no-alert
  if (window.confirm('This action will clear your cart. Continue?')) {
    await clearCart();
  }
};

const confirmedDeleteCart = useCallback(deleteCart);

const onDeleteCart = async () => {
  if (
    // eslint-disable-next-line no-alert
    window.confirm(
      'This action will delete your cart! If you copied an order and need to retain address or payment info, consider the "Clear Item" button instead. Continue?',
    )
  ) {
    await confirmedDeleteCart.execute();
  }
};

const onFreeShippingQualificationChange = (isQualified: boolean) => {
  freeShippingQualified.value = isQualified;
};

const shippingCutoff = ref<ShippingCutoff>();

const { loadFromConfig, pageContext, recommendationRows } = useRecommendationsFromConfig(
  '[CONFIG] Cart - Rows',
  route,
);
watchEffect(() => {
  pageContext.data = lineItems.value.map((i) => `0${i.variant.sku}`);
});

const refreshContent = useCallback(async () => {
  await loadCart();
  await loadLineItemExpansions();
  if (lineItems.value.length) {
    shippingCutoff.value = await getPredictedShippingCutoff();
  } else {
    shippingCutoff.value = undefined;
  }
  loadFromConfig();
});

watch(
  () => lineItems.value.length,
  (newLength, oldLength) => {
    if (newLength > oldLength) {
      removedLineItem.value = undefined;

      // when a new line item is added from the CartAddedModal, it must be expanded
      refreshContent.execute();
    }
  },
);

const handleStartAutoDelivery = (lineItemId: string) => {
  autoDeliverySignUp.itemId = lineItemId;
  shownModal.value = 'ad-sign-up';
};

const closeAutoDeliverySignUp = () => {
  shownModal.value = undefined;
  autoDeliverySignUp.interval = 6;
  autoDeliverySignUp.itemId = undefined;
};

const startAutoDelivery = () => {
  if (!autoDeliverySignUpLineItem.value) return;

  updateLineItem(autoDeliverySignUpLineItem.value.id, {
    autoDeliveryInterval: autoDeliverySignUp.interval * 7,
    autoDeliveryOfferLocation: 'Cart',
    autoDeliveryOfferType: 'Standard',
    distributionChannelKey: autoDeliveryChannel.value.key,
  });
  closeAutoDeliverySignUp();
};

onServerPrefetch(async () => {
  setNotification({
    key: 'couponMessage',
    message: belowThresholdMessage.value,
    messageType: 'notices',
  });
});

const globalError = templateRef<InstanceType<typeof GlobalErrorNotification>>('globalError');

const tagIds = computed(
  () =>
    new Set(
      lineItems.value.flatMap((li) => {
        const tagReferences =
          li.variant.attributes?.find((a) => a.name === 'tagReferences')?.value || [];
        return tagReferences.map((tag: { id: string; value: TagReference }) => tag.id);
      }),
    ),
);

type BadgeKey = `badge-${string}`;

const badgeTags = ref<Map<string, BadgeKey>>(new Map());
const chat = useChat();

onMounted(() => {
  chat.init();

  const atLeastSmallScreen = useMediaQuery('(min-width: 650px)');
  const errorSeenCutoff = computed(() => (atLeastSmallScreen.value ? 0.6 : 0.8));
  const globalErrorIntersectionRatio = ref<number>();
  const globalErrorObserved = ref(false);
  useIntersectionObserver(
    globalError,
    (entries) => {
      globalErrorIntersectionRatio.value = entries.pop()?.intersectionRatio;
    },
    { rootMargin: '-32px 0px -32px 0px', threshold: [0.59, 0.6, 0.61, 0.79, 0.8, 0.81] },
  );
  watchEffect(() => {
    if (updateLineItemFailure.value) {
      if ((globalErrorIntersectionRatio.value ?? 0) > errorSeenCutoff.value) {
        globalErrorObserved.value = true;
      }
      if (
        globalErrorIntersectionRatio.value !== undefined &&
        globalErrorIntersectionRatio.value <= errorSeenCutoff.value &&
        !globalErrorObserved.value
      ) {
        scrollToTarget(globalError.value.$el);
      }
    } else {
      globalErrorObserved.value = false;
      globalErrorIntersectionRatio.value = undefined;
    }
  });

  sendCartViewedEvent(
    lineItems.value,
    estimatedAmountToCharge.value,
    route.path,
    getCartId(cart.value),
  );

  refreshContent.execute();

  watchDebounced(
    tagIds,
    async () => {
      const productTags = await productTagsByIds(Array.from(tagIds.value) as string[]);
      badgeTags.value = new Map(
        productTags
          .filter((tag): tag is CustomObject & { value: { name: BadgeKey } } =>
            tag.value.name.startsWith('badge-'),
          )
          .map((tag) => [tag.id, tag.value.name]),
      );
    },
    { debounce: 300, immediate: true },
  );

  watch(
    isMobile,
    () => {
      if (flags.cartLayoutUpdates) {
        if (isMobile.value) {
          chat.registerSuppression();
        } else {
          chat.unregisterSuppression();
        }
      }
    },
    { immediate: true },
  );
});

onUnmounted(() => {
  if (flags.cartLayoutUpdates && isMobile.value) {
    chat.unregisterSuppression();
  }
});

whenever(
  () => !!customerStore.customer,
  () => {
    autoDeliveryStore.getActiveSubscriptions();
    autoDeliveryStore.getImpulseUpsellEligible();
    if (!cxMode.value) {
      customerStore.loadCustomerInfo();
    }
  },
  {
    immediate: true,
  },
);

const greetingCardsModal: GreetingCardsModalModels & GreetingCardsModalProps = reactive({
  isOpen: false,
  lineItem: undefined,
  options: [],
  step: 'select',
});
onMounted(async () => {
  greetingCardsModal.options = (await fetchGreetingCards()).filter(
    (variant) => !variant.backordered,
  );
});
const selectGreetingCardLineItemToEdit = (lineItem: GreetingCardsModalProps['lineItem']) => {
  greetingCardsModal.lineItem = lineItem;
  greetingCardsModal.step = 'edit';
  greetingCardsModal.isOpen = true;
};
whenever(
  () => !greetingCardsModal.isOpen,
  () => {
    greetingCardsModal.lineItem = undefined;
    greetingCardsModal.step = 'select';
  },
);
const greetingCardToastNotification = ref<string>();
// purposely not reactive; we don't want it to shift based on changes in cart state
const showGreetingCardsAboveTheFold =
  flags.pinGreetingCards ||
  containsGiftPurchase(lineItems.value.filter((l) => !isGreetingCardLineItem(l)));

const productDiscountNotice = computed(() => {
  const listPrice = autoDeliverySignUpLineItem.value?.variant.prices?.find((p) => !p.channel);

  if (!listPrice?.discounted) return undefined;

  let message = 'The discounted price does not qualify for Auto-Delivery subscription';
  if (!autoDeliverySignUpLineItem.value?.variant.wholesale) {
    message += `, but you can still enjoy ${autoDeliveryChannel.value.discount}% off of the listing price`;
  }

  return `${message}.`;
});

defineOptions({
  async criticalData(forwardedStore: Store<any>) {
    const forwardedStoreCart = useCart(forwardedStore);
    if (typeof window === 'undefined') {
      await forwardedStoreCart.loadCart();
      await forwardedStoreCart.loadLineItemExpansions();
    } else {
      forwardedStoreCart.loadCart(true);
    }
  },
});

const CREDIT_CARD_LOGO_TYPES = [
  'apple-pay',
  'paypal',
  'shop-pay',
  'american-express',
  'discover',
  'mastercard',
  'visa',
];

const previousRouteOrHomepage = computed(() => String(router.options.history.state?.back ?? '/'));
</script>

<template>
  <div
    :class="[
      flags.cartLayoutUpdates ? 'md:pb-4' : 'pt-0 pb-0 page-content cart ws-sm:pt-[25px]',
      { 'page-fade-manual-transition page-fade-enter': refreshContent.isPending },
    ]"
  >
    <div :class="flags.cartLayoutUpdates ? 'flex' : 'container'">
      <component
        :is="flags.cartLayoutUpdates ? Container : 'div'"
        :class="
          flags.cartLayoutUpdates
            ? 'w-full flex flex-col md:grid md:grid-cols-12 md:gap-10 relative'
            : 'flex-column ws-md:flex-row'
        "
        data-test="cart-container"
      >
        <div
          class="w-full"
          :class="
            flags.cartLayoutUpdates ? 'md:col-span-7 xl:col-span-8' : 'ws-md:w-2/3 content-section'
          "
        >
          <ShippingMessage
            v-if="!flags.cartLayoutUpdates"
            class="mt-2 ws-sm:mt-0 ws-sm:mx-0"
            :freeShippingThreshold
            :totalPrice="estimatedTotal"
            @free-shipping-qualified="onFreeShippingQualificationChange"
          >
            <GreetingCardUpsell
              v-if="showGreetingCardsAboveTheFold"
              class="mt-2.5 ws-lg:mt-0 w-full"
              :class="{ 'md:hidden': flags.cartLayoutUpdates }"
              :flags
              @add-greeting-card="greetingCardsModal.isOpen = true"
            />
          </ShippingMessage>
          <transition name="fade">
            <GlobalErrorNotification v-if="updateLineItemFailure" ref="globalError" class="my-8" />
          </transition>
          <div
            class="flex flex-row justify-between mt-6 md:mt-8"
            :class="{
              'border-neutral-300 ws-md:border-b border-solid mb-0 ws-md:mb-[34px]':
                !lineItems.length && !flags.cartLayoutUpdates,
            }"
          >
            <Header3 v-if="flags.cartLayoutUpdates" data-test="cart-header" headerTag="h1">
              Your Cart ({{ lineItems.length }})
            </Header3>
            <h2 v-else class="flex w-full md:mb-0" data-test="cart-header">
              Shopping Cart
              <template v-if="lineItems.length">({{ lineItems.length }})</template>
            </h2>
            <div
              v-if="!flags.cartLayoutUpdates && lineItems.length"
              class="self-center hidden-xs hidden-sm pb-[18px] shrink-0"
            >
              <RouterLink
                to="/"
                class="text-base font-semibold leading-6 underline color-inherit"
                data-test="continue-shopping-link"
              >
                Continue Shopping
              </RouterLink>
            </div>
          </div>
          <ActionBlock
            v-if="lineItems.length"
            class="mobile-action-block"
            :class="flags.cartLayoutUpdates ? 'mt-3 md:hidden' : 'hidden-md hidden-lg'"
            :hideExpressCheckout="flags.cartLayoutUpdates"
            :totalCost="flags.cartLayoutUpdates ? estimatedTotal : undefined"
          >
            <button v-if="isAdmin" class="button secondary" @click="onClearCart">
              Clear Items
            </button>
            <ThemedButton
              v-if="isAdmin"
              class="w-full h-12 mt-1.5 rounded-md"
              :isLoading="confirmedDeleteCart.isPending"
              theme="red"
              @click="onDeleteCart"
            >
              Delete Cart
            </ThemedButton>
          </ActionBlock>
          <ShippingMessage
            v-if="flags.cartLayoutUpdates"
            class="mt-2 md:mt-0 md:mx-0"
            :freeShippingThreshold
            stackVertically
            :totalPrice="estimatedTotal"
            @free-shipping-qualified="onFreeShippingQualificationChange"
          >
            <GreetingCardUpsell
              v-if="showGreetingCardsAboveTheFold"
              class="w-full mt-2.5"
              :class="{ 'md:hidden': flags.cartLayoutUpdates }"
              :flags
              @add-greeting-card="greetingCardsModal.isOpen = true"
            />
          </ShippingMessage>
          <ShippingPredictor
            v-if="lineItems.length"
            class="w-full pt-10 pb-10 justify-content-center pl-55 pr-55 hidden-md hidden-lg"
          />
          <div
            v-if="lineItems.length"
            class="flex flex-col mt-3 md:mt-2"
            :class="flags.cartLayoutUpdates ? 'gap-6 md:gap-4' : 'gap-6 sm:gap-8'"
          >
            <LineItem
              v-for="(lineItem, index) in maybeReversedLineItemsWithRemoved"
              :badgeTags
              class="border-t border-solid border-neutral-300"
              :class="flags.cartLayoutUpdates ? 'pt-4 md:pt-3' : 'pt-6 sm:pt-8'"
              :key="lineItem.id"
              :lineItem
              :maxQuantity
              :readOnly="isGiftLineItem(lineItem)"
              :removeMode="lineItem === removedLineItem ? removedLineItem.removeMode : undefined"
              @adjust-auto-delivery="shownModal = 'ad-adjust'"
              @edit-greeting-card="selectGreetingCardLineItemToEdit"
              @remove="handleRemoveLineItem(lineItem, index)"
              @start-auto-delivery="handleStartAutoDelivery(lineItem.id)"
              @update="(changes) => updateLineItem(lineItem.id, changes)"
            />
          </div>
          <div
            v-if="!lineItems.length"
            class="flex-column mt-2.5"
            :class="{
              'items-center pt-12 md:pt-20 text-neutral-500 border-t border-solid border-neutral-300':
                flags.cartLayoutUpdates,
            }"
            data-test="empty-cart-contents"
          >
            <template v-if="flags.cartLayoutUpdates">
              <Header4 class="normal-case" data-test="cart-is-empty" headerTag="h2">
                Your cart is empty
              </Header4>
              <RouterLink :to="previousRouteOrHomepage" class="mt-2.5 underline color-inherit">
                <BaseBodyText>Find something that satisfies your cravings here</BaseBodyText>
              </RouterLink>
              <BaseBodyText v-if="userFirstName" class="mt-2.5">
                Reorder your usuals easily with
                <RouterLink to="/account/easy-reorder" class="underline color-inherit">
                  Easy Order
                </RouterLink>
              </BaseBodyText>
              <BaseBodyText v-else class="flex mt-2.5 text-neutral-500" data-test="sign-in-prompt">
                Saved items in your cart?&nbsp;
                <UnstyledButton
                  class="underline text-neutral-500"
                  data-test="sign-in-link"
                  @click="handleOpen"
                >
                  <BaseBodyText>Sign In</BaseBodyText>
                </UnstyledButton>
              </BaseBodyText>
            </template>
            <template v-else>
              <h3
                class="order-2 mb-2.5 leading-[18px] ws-md:leading-[22px] ws-md:mb-[26px] ws-md:order-1 text-lg ws-md:text-[22px]"
                data-test="cart-is-empty"
              >
                Your cart is empty
              </h3>
              <span
                class="inline-block text-white order-1 mb-12 ws-md:max-w-[330px] ws-md:mb-[28px] ws-md:order-2"
              >
                <RouterLink
                  to="/"
                  class="w-full color-inherit bg-nuts-neutral-900 py-3 button leading-[17px] ws-md:leading-5 text-sm ws-md:text-base"
                  data-test="continue-shopping-button"
                >
                  Continue Shopping
                </RouterLink>
              </span>
              <p
                v-if="!userFirstName"
                class="flex order-2 leading-4 mb-9 text-[13px]"
                :class="
                  flags.cartLayoutUpdates
                    ? 'mt-2.5 md:leading-[18px] md:mb-[42px] md:text-[15px]'
                    : 'ws-md:leading-[18px] ws-md:mb-[42px] ws-md:text-[15px]'
                "
                data-test="sign-in-prompt"
              >
                Did you have something in your cart?&nbsp;
                <RouterLink to="/sign/in" class="underline color-inherit" data-test="sign-in-link">
                  Sign In
                </RouterLink>
              </p>
            </template>
          </div>
          <div
            v-else
            class="flex-row justify-end border-t border-solid border-neutral-300"
            :class="
              flags.cartLayoutUpdates
                ? 'mt-4 md:pt-6 md:justify-between'
                : 'mt-6 cx-info ws-md:justify-between'
            "
          >
            <RouterLink
              to="/"
              class="text-base font-semibold underline color-inherit hidden-xs hidden-sm"
              data-test="continue-shopping-link"
            >
              Continue Shopping
            </RouterLink>
            <span v-if="!flags.cartLayoutUpdates" class="text-sm" data-test="cart-lookup-id">
              Your Cart ID:&thinsp;
              <span class="italic font-semibold text-nuts-cyan-600">{{ cartKey }}</span>
            </span>
          </div>
          <GreetingCardUpsell
            v-if="!showGreetingCardsAboveTheFold"
            :class="[
              { 'ws-md:mb-6': !recommendationRows.some((row) => recommendations(row)?.length) },
              flags.cartLayoutUpdates
                ? lineItems.length
                  ? 'mt-4 md:hidden'
                  : 'mt-12 md:hidden'
                : 'mb-6 mt-1 ws-md:my-0',
            ]"
            :flags
            @add-greeting-card="greetingCardsModal.isOpen = true"
          />
        </div>
        <div
          :class="
            flags.cartLayoutUpdates
              ? 'md:col-span-5 xl:col-span-4'
              : 'ws-md:w-1/3 sidebar-section -mx-[15px] ws-sm:mx-0 max-w-none'
          "
        >
          <div
            :class="
              flags.cartLayoutUpdates
                ? 'md:sticky w-full mt-6 md:mt-8 md:top-28 lg:top-16'
                : 'sticky flex-col'
            "
          >
            <ShippingPredictor
              class="w-full hidden-xs hidden-sm"
              :class="
                flags.cartLayoutUpdates
                  ? 'px-4 py-3 border border-solid border-neutral-300 rounded-2xl'
                  : 'px-14 py-2.5 justify-content-center bg-nuts-stone-100'
              "
              :shippingCutoff
            />
            <AdminDiscountCodeForm class="bg-nuts-stone-100" />
            <div
              :class="{
                'text-sm md:px-4 md:py-6 sm:mx-0 md:border md:border-solid md:rounded-2xl md:border-neutral-300':
                  flags.cartLayoutUpdates,
                'ptn-cart-summary': !flags.cartLayoutUpdates,
              }"
            >
              <CostSummaryRedesign
                v-if="flags.cartLayoutUpdates"
                :combinedSavings
                :containsAutoDelivery
                :containsProductDiscounts
                :freeShipping="freeShippingQualified"
                :subTotalPrice="preCheckoutSubtotal"
                :totalDiscountedAmount="cartDiscountAmount"
                :totalPrice="estimatedTotal"
                @refresh-content="refreshContent.execute"
              />
              <CostSummary
                v-else
                class="mx-auto mb-2.5 ws-md:mb-0 ws-md:mx-3`"
                :combinedSavings
                :containsAutoDelivery
                :containsProductDiscounts
                :freeShipping="freeShippingQualified"
                :subTotalPrice="preCheckoutSubtotal"
                :totalDiscountedAmount="cartDiscountAmount"
                :totalPrice="estimatedTotal"
                @refresh-content="refreshContent.execute"
              />
              <ActionBlock
                v-if="lineItems.length"
                class="mt-5"
                :class="flags.cartLayoutUpdates ? 'pt-2' : 'sidebar-action-block ws-md:mx-3'"
                :totalCost="estimatedTotal"
              >
                <div v-if="isAdmin" class="flex flex-row gap-2 mt-3 md:flex-col">
                  <ThemedButton class="w-full bg-orange-600" @click="onClearCart">
                    Clear Items
                  </ThemedButton>
                  <ThemedButton
                    class="w-full"
                    :isLoading="confirmedDeleteCart.isPending"
                    theme="red"
                    @click="onDeleteCart"
                  >
                    Delete Cart
                  </ThemedButton>
                </div>
              </ActionBlock>
              <template v-if="flags.cartLayoutUpdates">
                <div
                  class="flex flex-row flex-wrap gap-2 pt-3 mt-3 border-t border-solid border-neutral-200"
                  data-test="cart-payment-method-options"
                >
                  <SmallBodyText class="w-full shrink-0 text-neutral-500">
                    Supported payment methods
                  </SmallBodyText>
                  <CreditCardLogo
                    v-for="cardType in CREDIT_CARD_LOGO_TYPES"
                    :key="cardType"
                    :cardType
                    class="h-6"
                  />
                </div>
                <Transition name="fade">
                  <UnstyledButton
                    v-if="chat.available"
                    class="flex items-center mt-3 underline"
                    @click="chat.open"
                  >
                    <SmallBodyText>Have questions? Get help with Live Chat</SmallBodyText>
                  </UnstyledButton>
                </Transition>
                <SmallBodyText v-if="lineItems.length" class="mt-1" data-test="cart-lookup-id">
                  Your Cart ID:&thinsp;
                  <span class="italic font-semibold text-nuts-cyan-600">{{ cartKey }}</span>
                </SmallBodyText>
              </template>
              <template v-else>
                <br />
                <RouterLink
                  to="/"
                  class="w-full text-center ptn-continue-shopping-link"
                  data-test="continue-shopping-link"
                >
                  Continue Shopping
                </RouterLink>
              </template>
            </div>
            <GreetingCardUpsell
              v-if="flags.cartLayoutUpdates"
              class="hidden mt-4 md:block md:visible"
              :flags
              @add-greeting-card="greetingCardsModal.isOpen = true"
            />
            <div
              v-if="!flags.cartLayoutUpdates"
              v-once
              id="acadia-usps-row"
              data-test="shipping-images"
            />
            <SupportInfo v-if="!flags.cartLayoutUpdates" class="sidebar-info-block" />
          </div>
        </div>
      </component>
    </div>
    <component
      :is="flags.cartLayoutUpdates ? Container : 'div'"
      :class="{ 'recommendations-wrapper': !flags.cartLayoutUpdates }"
      data-test="cart-container"
    >
      <div class="recommendations-container bkg-pampas">
        <div
          v-for="(row, i) in recommendationRows"
          :key="'dySelector' in row ? row.dySelector : i"
          :class="{ container: !flags.cartLayoutUpdates }"
        >
          <ProductRecommendations
            :analyticsList="row.analyticsList"
            :analyticsMetadata="analyticsMetadata(row)"
            :decisionId="decisionId(row)"
            :enableAddToCart="row.enableAddToCart"
            :link="row.link"
            :list="recommendations(row)"
            pageSection="Product Recommendations"
            :spacingDirection="i === recommendationRows.length - 1 ? 'vertical' : 'top'"
            spacingLevel="md"
            :title="title(row)"
            trackingEventLocation="CART"
          />
        </div>
      </div>
    </component>
    <AutoDeliveryAdjustFrequencyModal
      v-if="autoDeliveryInterval"
      v-model="autoDeliveryInterval"
      :isOpen="shownModal === 'ad-adjust'"
      :lineItems="autoDeliveryLineItems"
      @handle-close="shownModal = undefined"
    />
    <AutoDeliveryModal
      v-model:interval="autoDeliverySignUp.interval"
      v-model:step="autoDeliverySignUp.step"
      :isOpen="shownModal === 'ad-sign-up'"
      :productDiscountNotice
      @handle-close="closeAutoDeliverySignUp"
      @start-auto-delivery="startAutoDelivery"
    >
      <template v-if="autoDeliverySignUpLineItem && nextDelivery">
        <BaseBodyText
          v-if="activeSubscriptionForSignUpLineItem"
          class="px-8 py-4 mt-4 rounded-lg text-nuts-neutral-800 bg-nuts-sky-100"
        >
          Auto-Delivery is already active for this product, next shipment is on
          <b>{{ dayjs(activeSubscriptionForSignUpLineItem.nextShipOn).format('MMMM Do') }}</b
          >.
          <RouterLink
            class="underline"
            :to="getAutoDeliveryDetailsPath(activeSubscriptionForSignUpLineItem)"
          >
            Manage Auto-Delivery
          </RouterLink>
        </BaseBodyText>
        <SmallBodyText v-else class="mt-5 font-semibold text-nuts-sky-800">
          Want to add this product to your existing Auto-Delivery?<br />Visit the
          <component
            :class="[
              'auto-delivery-sign-up-notice',
              { underline: showLinkForAutoDeliverySignUpLineItem },
            ]"
            :is="showLinkForAutoDeliverySignUpLineItem ? 'router-link' : 'span'"
            :to="
              showLinkForAutoDeliverySignUpLineItem
                ? autoDeliverySignUpLineItem.productPath ?? '#'
                : undefined
            "
          >
            product page</component
          >.
        </SmallBodyText>
      </template>
    </AutoDeliveryModal>
    <GreetingCardsModal
      v-model:isOpen="greetingCardsModal.isOpen"
      v-model:step="greetingCardsModal.step"
      :lineItem="greetingCardsModal.lineItem"
      :options="greetingCardsModal.options"
      @new-line-added="greetingCardToastNotification = 'Greeting card added to your cart'"
      @handle-close="greetingCardsModal.isOpen = false"
    />
    <ToastNotification
      :show="!!greetingCardToastNotification"
      @hide="greetingCardToastNotification = ''"
    >
      <BaseBodyText>{{ greetingCardToastNotification }}</BaseBodyText>
    </ToastNotification>
  </div>
</template>

<style lang="scss" scoped>
$fontSize: 14px;
.cx-info {
  padding: 20px 0;
  @include respond-min($screen-md-min) {
    padding: 26px 0 60px;
  }
}
.mobile-action-block {
  :deep(.apple-pay) {
    height: 39px;
  }
  :deep(.button) {
    font-size: 14px;
    font-weight: 600;
    line-height: 15px;
    margin: 5px 0;
    padding: 15px 0;
    width: 100%;
  }
}
.ptn-cart-summary {
  background-color: $pampas;
  font-size: $fontSize;
  width: 100%;
  max-width: unset;
  padding: 18px 18px 0;
  @include respond-min($screen-md-min) {
    padding: 24px 18px;
  }
  :deep(.ptn-continue-shopping-link) {
    display: inline-block;
    font-size: 14px;
    @include respond-min($screen-md-min) {
      display: none;
    }
  }
}
.recommendations-wrapper {
  overflow: hidden;
  .recommendations-container {
    margin: -40px;
    padding: 40px;
    @include respond-max($screen-md-max) {
      .container {
        padding-right: 0px;
      }
    }
  }
}
.sidebar-info-block {
  justify-content: center;
  padding: 20px;
  @include respond-min($screen-md-min) {
    padding: 20px 20px 50px;
  }
}
.headerRedesign {
  .cart {
    &.page-content {
      .container {
        .sidebar-section {
          .sticky {
            @include respond-min($screen-md-min) {
              top: 130px;
            }
            @include respond-min($screen-lg-min) {
              top: 90px;
            }
          }
        }
      }
    }
  }
}
.auto-delivery-sign-up-notice {
  @apply text-nuts-sky-800;
}
</style>
